h<script setup>
import {useUserStore} from "../../store/users.js";
import {onMounted} from "vue";
import {useModalStore} from "../../store/modal.js";
import {useAuthStore} from "../../store/auth.js";

const notificationSound = new Audio('/sounds/notification.mp3');
notificationSound.preload = 'auto';

let audioUnlocked = false;

const unlockAudio = async () => {
    if (audioUnlocked) return;

    try {
        // Play silently to unlock audio
        notificationSound.volume = 0;
        await notificationSound.play();
        notificationSound.pause();
        notificationSound.currentTime = 0;
        notificationSound.volume = 1;

        audioUnlocked = true;
        console.log('🔓 Notification audio unlocked');
    } catch (e) {
        console.warn('Audio unlock failed', e);
    }
};

const userStore = useUserStore()
const modalStore = useModalStore()
const authStore = useAuthStore()

onMounted(async () => {
    window.addEventListener('click', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });

    await userStore.getAllUsers();
    await authStore.getNotifications();

    window.Echo
        .private(`App.Models.User.${authStore.user.id}`)
        .notification(async () => {
            if (audioUnlocked) {
                notificationSound.currentTime = 0;
                notificationSound.play();
            }

            await authStore.getNotifications();
        });
});
</script>

<template>
    <header>

        <router-link to="/" class="logo">
            <img src="/public/internal/images/orb.png" srcset="/public/internal/images/orb@2x.png 2x" alt="">
        </router-link>

        <nav>
            <router-link to="/">
                <ah-icon name="building-home"/>
                <span class="label">Home</span></router-link>
            <router-link to="/orders">
                <ah-icon name="cart-default"/>
                <span class="label">Orders</span></router-link>
            <router-link to="/tickets">
                <ah-icon name="paper-slip"/>
                <span class="label">Tickets</span></router-link>
            <router-link to="/calls">
                <ah-icon name="call-voice"/>
                <span class="label">Calls</span></router-link>
            <router-link to="/chats">
                <ah-icon name="speechbubble-sms"/>
                <span class="label">Chats</span></router-link>
            <router-link to="/users">
                <ah-icon name="user-group"/>
                <span class="label">Users</span></router-link>
        </nav>

        <div class="profile">
            <brand-nav-button
                :class="['notifications-button', {'--has-unread-notifications': authStore.unread_notifications_count}]"
                variant="icon-sm" icon="notify-default" @click="modalStore.open('Notifications', {}, 'header')"/>
            <brand-nav-button variant="icon" icon="menu-burger" @click="modalStore.open('Menu', {}, 'header')"/>
        </div>

    </header>
</template>

<style lang="scss">
header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 7rem;
    padding: 0 4rem;

    @include dark() {
        background: #000;
    }

    nav {
        display: flex;
        justify-content: space-between;
        align-items: center;

        a {
            height: 4.8rem;
            min-width: 4.8rem;
            border-radius: 1rem;
            background: transparent;
            display: flex;
            align-items: center;
            transition: background-color .2s ease;

            + a {
                margin-left: 2rem;
            }

            .icon {
                height: 4.8rem;
                width: 4.8rem;
                fill: #fff;
                display: flex !important;
                align-items: center;
                justify-content: center;

                svg {
                    height: 2.4rem;
                    width: 2.4rem;
                }
            }


            .label {
                max-width: 0;
                overflow: hidden;
                transition: all .2s ease;
                @include font(1.4, null, black);
                color: #fff;
            }

            @include hover() {
                background: rgba(#fff, .2);
            }

            &.router-link-active {
                background: var(--color-red-raspberry);

                .label {
                    max-width: 50rem;
                    padding-right: 1.2rem;
                }
            }
        }
    }

    .profile {
        display: flex;
        align-items: center;
        gap: 2rem;

        .notifications-button {
            position: relative;

            &.--has-unread-notifications {
                &::before {
                    content: '';
                    position: absolute;
                    top: -.5rem;
                    right: -.5rem;
                    width: 1rem;
                    height: 1rem;
                    border-radius: 50%;
                    background: var(--color-red-raspberry);
                }
            }
        }
    }

    @include mobile() {
        padding: 0 1rem;

        .logo {
            display: none;
        }

        nav {
            border-radius: 2.4rem;
            background: var(--color-darth-vader);

            a {
                + a {
                    margin-left: 0;
                }

                &.router-link-active {
                    background: var(--color-red-raspberry);
                    border-radius: 50%;

                    .label {
                        max-width: 0;
                        padding-right: 0;
                    }
                }
            }
        }

        .profile {
            gap: .5rem;
        }
    }
}


</style>
